import { Box, BoxProps, Typography, styled } from "@mui/material";
import { rem } from "polished";
import React from "react";

import { ariaLabel } from "src/constants";
import { hasDigit, hasLowerCase, hasUpperCase, isLengthEnought } from "src/utils";

import { Icon } from "../Icon";

type Condition = {
  test: (password: string) => boolean;
  message: string;
  validMessage: string;
  invalidMessage: string;
};

const conditions: Array<Condition> = [
  {
    test: hasUpperCase,
    message: "Doit contenir une majuscule",
    validMessage: ariaLabel.passwordContainsUpperCase,
    invalidMessage: ariaLabel.passwordDoesNotContainUpperCase,
  },
  {
    test: hasLowerCase,
    message: "Doit contenir une minuscule",
    validMessage: ariaLabel.passwordContainsLowerCase,
    invalidMessage: ariaLabel.passwordDoesNotContainLowerCase,
  },
  {
    test: hasDigit,
    message: "Doit contenir un chiffre",
    validMessage: ariaLabel.passwordContainsDigit,
    invalidMessage: ariaLabel.passwordDoesNotContainDigit,
  },
  {
    test: isLengthEnought,
    message: "Etre composé d'un minimum de 8 caractères",
    validMessage: ariaLabel.passwordIsLongEnough,
    invalidMessage: ariaLabel.passwordIsNotLongEnough,
  },
];

export const StyledIconContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: ${(props) => props.theme.spacing(2)};
  width: ${rem(25)};
  height: ${rem(25)};
  border-radius: 50%;
`;

export const StyledIcon = styled(Icon)`
  fill: ${(props) => props.theme.palette.common.white};
`;

interface Props extends BoxProps {
  password: string;
}

export const PasswordStrengthIndicator = ({ password, ...boxProps }: Props) => {
  const tests = conditions.map((condition) => {
    return {
      ...condition,
      result: condition.test(password),
    };
  });

  return (
    <Box {...boxProps} sx={{ p: 2, backgroundColor: "primary.light", ...boxProps.sx }}>
      <Box id="password-strength-indicator">
        <Typography>
          Pour la <b>sécurité de votre compte</b>, votre mot de passe :
        </Typography>
        {tests.map((test, index) => {
          const isInputEmpty = password.length === 0;
          let iconLabel: undefined | string;
          if (!isInputEmpty) {
            iconLabel = test.result ? test.validMessage : test.invalidMessage;
          }
          return (
            <Box
              data-cy-password-test={test.test.name}
              data-cy-password-valid={test.result}
              key={index}
              sx={{ display: "flex", alignItems: "center", mt: 1 }}
            >
              <StyledIconContainer
                aria-live="polite"
                aria-atomic="true"
                sx={{ backgroundColor: test.result ? "success.main" : "gray.subnavTab" }}
                aria-label={iconLabel}
              >
                <StyledIcon name="check" size={16} />
              </StyledIconContainer>
              <Typography
                sx={{
                  fontWeight: "bold",
                  color: test.result ? "secondary.main" : "gray.subnavTab",
                }}
              >
                {test.message}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
